/* oxlint-disable max-lines */
import * as React from 'react';
import { Button, FormField, Select, UIHeading1, useDeviceType } from '@cian/ui-kit';
import { clsx } from 'clsx';
import { useCallback, useMemo } from 'react';

import { ERegion } from 'shared/types/form';
import { EOfferType, EStatus, IOffer } from 'shared/types/listing/listing';
import {
  formatArea,
  formatDate,
  formatLocation,
  formatNumber,
  formatObjectType,
  formatReadyAccommodate,
  formatRoomsCount,
  formatStatus,
  formatStringArray,
} from 'shared/utils/formatters';
import * as styles from './DobroListing.css';

import { EReadyAccommodate } from 'shared/repositories/cian-dobro/entities/property/OfferV2Schema';
import { FILTER_REGIONS, FILTER_STATUSES } from './constants';

export type TCountByStatus = Record<EStatus, number>;

export interface IDobroListingProps {
  region: ERegion | null;
  setRegion: (value: ERegion | null) => void;
  status: EStatus | null;
  setStatus: (status: EStatus | null) => void;
  readyAccommodate: EReadyAccommodate | null;
  setReadyAccommodate: (readyAccommodate: EReadyAccommodate | null) => void;
  offerType: EOfferType | null;
  setOfferType: (status: EOfferType | null) => void;
  offers: IOffer[];
  openModal: (offer: IOffer) => void;
  offerTypeCounts: { all: number; assignedCount: number; unassignedCount: number };
  anyRegionCount: number;
  countByRegion: Record<ERegion, number>;
  anyStatusCount: number;
  countByStatus: Record<EStatus, number>;
  countByReadyAccommodate: { all: number } & { [key in EReadyAccommodate]: number };
  isCustomerServiceAccount: boolean;
  applicationProcessingComponent(offerId: number, assignedFund: boolean): React.ReactNode;
}

function formatCount(count: number) {
  return ` (${count})`;
}

export function DobroListing(props: IDobroListingProps) {
  const {
    offers,
    openModal,
    region,
    setRegion,
    status,
    setStatus,
    readyAccommodate,
    setReadyAccommodate,
    offerType,
    setOfferType,
    offerTypeCounts,
    anyRegionCount,
    countByRegion,
    anyStatusCount,
    countByStatus,
    countByReadyAccommodate,
    isCustomerServiceAccount,
    applicationProcessingComponent,
  } = props;
  const deviceType = useDeviceType();

  const onRegionChange = useCallback(
    (region: ERegion) => {
      setRegion(region ?? null);
    },
    [setRegion],
  );

  const onStatusChange = useCallback(
    (status: EStatus) => {
      setStatus(status ?? null);
    },
    [setStatus],
  );

  const onReadyAccommodateChange = useCallback(
    (readyAccommodate: EReadyAccommodate) => {
      setReadyAccommodate(readyAccommodate ?? null);
    },
    [setReadyAccommodate],
  );

  const onOfferTypeChange = useCallback(
    (offerType: EOfferType) => {
      setOfferType(offerType ?? null);
    },
    [setOfferType],
  );

  const isMobile = useMemo(() => deviceType !== 'desktop', [deviceType]);

  const regionOptions = useMemo(() => {
    const emptyFilter = { value: '', label: 'Любой' + formatCount(anyRegionCount) };
    const filters = FILTER_REGIONS.map(value => ({
      label: `${formatLocation(value)} ${formatCount(countByRegion[value])}`,
      value,
    }));

    return [emptyFilter, ...filters];
  }, [anyRegionCount, countByRegion]);

  const statusOptions = useMemo(() => {
    const emptyFilter = { value: '', label: 'Любой' + formatCount(anyStatusCount) };
    const filters = FILTER_STATUSES.map(value => ({
      label: `${formatStatus(value)} ${formatCount(countByStatus[value])}`,
      value,
    }));

    return [emptyFilter, ...filters];
  }, [anyStatusCount, countByStatus]);

  const offerTypeOptions = useMemo(() => {
    return [
      { value: '', label: `Любой (${offerTypeCounts.all})` },
      { value: EOfferType.InWork, label: `В работе (${offerTypeCounts.assignedCount})` },
      { value: EOfferType.FreeToApply, label: `Нераспределённый (${offerTypeCounts.unassignedCount})` },
    ];
  }, [offerTypeCounts.all, offerTypeCounts.assignedCount, offerTypeCounts.unassignedCount]);

  const readyAccommodateOptions = useMemo(() => {
    return [
      { value: '', label: `Всех (${countByReadyAccommodate.all})` },
      {
        value: EReadyAccommodate.During_treatment,
        label: `${formatReadyAccommodate(EReadyAccommodate.During_treatment)} (${countByReadyAccommodate[EReadyAccommodate.During_treatment]})`,
      },
      {
        value: EReadyAccommodate.Fire,
        label: `${formatReadyAccommodate(EReadyAccommodate.Fire)} (${countByReadyAccommodate[EReadyAccommodate.Fire]})`,
      },
      {
        value: EReadyAccommodate.Refugees,
        label: `${formatReadyAccommodate(EReadyAccommodate.Refugees)} (${countByReadyAccommodate[EReadyAccommodate.Refugees]})`,
      },
      {
        value: EReadyAccommodate.River_flooding,
        label: `${formatReadyAccommodate(EReadyAccommodate.River_flooding)} (${countByReadyAccommodate[EReadyAccommodate.River_flooding]})`,
      },
    ];
  }, [countByReadyAccommodate]);

  const ListingComponent = isMobile ? DobroListingMobile : DobroListingDesktop;

  return (
    <div className={clsx(styles.page, isMobile ? styles.mobile : styles.desktop)}>
      <div className={clsx(styles.heading, isMobile && styles.mobile)}>
        <UIHeading1>Заявки в Циан.Добро</UIHeading1>
      </div>

      <div className={clsx(styles.form, isMobile && styles.mobile)} data-name="DobroListing-form">
        <div className={styles.form_control} data-name="DobroListing-form-region">
          <FormField label="Регион">
            <Select options={regionOptions} value={region || ''} onChange={onRegionChange} />
          </FormField>
        </div>

        <div className={styles.form_control} data-name="DobroListing-form-status">
          <FormField label="Статус">
            <Select options={statusOptions} value={status || ''} onChange={onStatusChange} />
          </FormField>
        </div>

        <div className={styles.form_control} data-name="DobroListing-form-status">
          <FormField label="Готов принять">
            <Select
              options={readyAccommodateOptions}
              value={readyAccommodate || ''}
              onChange={onReadyAccommodateChange}
            />
          </FormField>
        </div>

        {!isCustomerServiceAccount && (
          <div className={styles.form_control} data-name="DobroListing-form-offer-type">
            <FormField label="Тип заявки">
              <Select options={offerTypeOptions} value={offerType || ''} onChange={onOfferTypeChange} />
            </FormField>
          </div>
        )}
      </div>

      <ListingComponent
        applicationProcessingComponent={applicationProcessingComponent}
        openModal={openModal}
        offers={offers}
      />
    </div>
  );
}

function DobroListingDesktop({
  offers,
  openModal,
  applicationProcessingComponent,
}: {
  offers: IOffer[];
  openModal: (offer: IOffer) => void;
  applicationProcessingComponent(offerId: number, assignedFund: boolean): React.ReactNode;
}) {
  return (
    <table className={styles.desktop_table} data-testid="DobroListingDesktop-table">
      <thead>
        <tr>
          <th>Статус</th>
          <th>Дата создания</th>
          <th>Готов принять</th>
          <th>Тип жилья</th>
          <th>Адрес</th>
          <th>Площадь</th>
          <th>Кол-во комнат</th>
          <th>Кол-во гостей</th>
          <th>Кол-во спальных мест</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {offers.map((offer, index) => (
          <DobroListingDesktopItem
            applicationProcessingComponent={applicationProcessingComponent}
            key={index}
            offer={offer}
            openModal={openModal}
          />
        ))}
      </tbody>
    </table>
  );
}

function DobroListingDesktopItem({
  offer,
  openModal,
  applicationProcessingComponent,
}: {
  offer: IOffer;
  openModal: (offer: IOffer) => void;
  applicationProcessingComponent(offerId: number, assignedFund: boolean): React.ReactNode;
}) {
  const onClick = useCallback(() => {
    openModal(offer);
  }, [openModal, offer]);

  return (
    <tr data-testid="DobroListingDesktopItem">
      <td data-testid="DobroListingDesktopItem-status">{formatStatus(offer.status)}</td>
      <td data-testid="DobroListingDesktopItem-createdAt">{formatDate(offer.createdAt)}</td>
      <td data-testid="DobroListingDesktopItem-readyAccommodate">
        {formatStringArray(offer.readyAccommodate.map(formatReadyAccommodate))}
      </td>
      <td data-testid="DobroListingDesktopItem-objectType">{formatObjectType(offer.objectType)}</td>
      <td data-testid="DobroListingDesktopItem-address">
        <OfferLocation offer={offer} />
      </td>
      <td data-testid="DobroListingDesktopItem-area">{formatArea(offer.area)}</td>
      <td data-testid="DobroListingDesktopItem-roomsCount">{formatRoomsCount(offer.roomsCount)}</td>
      <td data-testid="DobroListingDesktopItem-guestsCount">{offer.guestsCount}</td>
      <td data-testid="DobroListingDesktopItem-bedsCount">{formatNumber(offer.bedsCount)}</td>
      <td>
        <div className={styles.action_buttons}>
          <Button onClick={onClick} theme="fill_secondary" fullWidth>
            Подробнее
          </Button>

          {applicationProcessingComponent(offer.id, offer.assignedFund)}
        </div>
      </td>
    </tr>
  );
}

function DobroListingMobile({
  offers,
  openModal,
  applicationProcessingComponent,
}: {
  offers: IOffer[];
  openModal: (offer: IOffer) => void;
  applicationProcessingComponent(offerId: number, assignedFund: boolean): React.ReactNode;
}) {
  return (
    <div>
      {offers.map((offer, index) => (
        <DobroListingMobileItem
          applicationProcessingComponent={applicationProcessingComponent}
          key={index}
          offer={offer}
          openModal={openModal}
        />
      ))}
    </div>
  );
}

function DobroListingMobileItem({
  offer,
  openModal,
  applicationProcessingComponent,
}: {
  offer: IOffer;
  openModal: (offer: IOffer) => void;
  applicationProcessingComponent(offerId: number, assignedFund: boolean): React.ReactNode;
}) {
  const onClick = useCallback(() => {
    openModal(offer);
  }, [openModal, offer]);

  return (
    <div className={styles.mobile_card}>
      <table className={styles.mobile_card_table}>
        <tbody>
          <tr>
            <th>Статус</th>
            <td>{formatStatus(offer.status)}</td>
          </tr>
          <tr>
            <th>Дата создания</th>
            <td>{formatDate(offer.createdAt)}</td>
          </tr>
          <tr>
            <th>Тип жилья</th>
            <td>{formatObjectType(offer.objectType)}</td>
          </tr>
          <tr>
            <th>Адрес</th>
            <td>
              <OfferLocation offer={offer} />
            </td>
          </tr>
          <tr>
            <th>Площадь</th>
            <td>{formatArea(offer.area)}</td>
          </tr>
          <tr>
            <th>Кол-во комнат</th>
            <td>{formatRoomsCount(offer.roomsCount)}</td>
          </tr>
          <tr>
            <th>Кол-во гостей</th>
            <td>{offer.guestsCount}</td>
          </tr>
          <tr>
            <th>Кол-во спальных мест</th>
            <td>{formatNumber(offer.bedsCount)}</td>
          </tr>
        </tbody>
      </table>
      <div className={clsx(styles.action_buttons, styles.mobile)}>
        <Button fullWidth theme="fill_secondary" onClick={onClick}>
          Подробнее
        </Button>

        {applicationProcessingComponent(offer.id, offer.assignedFund)}
      </div>
    </div>
  );
}

function OfferLocation({ offer }: { offer: IOffer }) {
  return (
    <>
      Город: <span className={styles.cut_text}>{formatLocation(offer.location)}</span>
      <br />
      Адрес: <span className={styles.cut_text}>{offer.address}</span>
      <br />
      Этаж: {formatNumber(offer.floor)}
    </>
  );
}
